import React, { useState, useEffect, useMemo } from 'react';
import CommonTable, { EllipsisWithTooltip } from '../Shared/Tables/TableV1.tsx';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { Grid, Box, CircularProgress, Typography, IconButton } from '@mui/material';
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router';

const StudentDashboard = () => {
  const { showSnackBar } = useSnackBar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Get user information from Recoil state
  const loggedInUser = useRecoilValue(userInformationAtom);
  const mobile = loggedInUser?.mobile || '';
  const userId = loggedInUser?.id || loggedInUser?.user_id || '';
  const loggedInUsername = loggedInUser?.username || '';
  const isSuperUser = loggedInUser?.is_superuser === true;

  // Data states
  const [examCalendar, setExamCalendar] = useState([]);
  const [syllabus, setSyllabus] = useState([]);
  const [schoolMarks, setSchoolMarks] = useState([]);
  const [holidayCalendar, setHolidayCalendar] = useState([]);
  const [timetableData, setTimetableData] = useState({});

  // Filter states
  const [examFilter, setExamFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('');
  const [selectedClass, setSelectedClass] = useState('6');
  const [holidayTypeFilter, setHolidayTypeFilter] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const aiActions = AiActions();
  const [studentClass, setStudentClass] = useState('');

  // Template form type mapping
  const formTypeMap = {
    0: 'exam_calendar_form',
    1: 'time_table_form',
    2: 'syllabus_form',
    3: 'school_marks_form',
    4: 'holidays_form',
  };


  const getEntityRecord = async (mobileNumber?: string) => {
    if (!mobileNumber) return;

    // const formattedMobile =
    //   mobileNumber.startsWith('91') ? mobileNumber : `91${mobileNumber}`;
    const formattedMobile = mobileNumber;
    try {
      const response = await aiActions.getRecordEntity(formattedMobile, {
        template_form_type: 'contact_form',
      });
      setData(response);
    } catch (error) {
      console.error('❌ Failed to get entity record', error);
    }
  };


  const handleClassGroupFromData = (entityData: any) => {
    if (!entityData?.items?.length || !loggedInUsername) return;

    entityData.items.map((item: any) => {
      if (
        item?.entity?.username?.toLowerCase() ===
        loggedInUsername.toLowerCase()
      ) {
        setStudentClass(item.entity.class);
      }
    });
  };

  useEffect(() => {
    if (data && loggedInUsername) {
      handleClassGroupFromData(data);
    }
  }, [data, loggedInUsername]);

  useEffect(() => {
    getEntityRecord(mobile);
  }, [mobile]);

  useEffect(() => {
    if (studentClass) {
      setSelectedClass(studentClass);

      if (isSuperUser) {
        setClassFilter('all');
      } else {
        setClassFilter(studentClass);
      }
    }
  }, [studentClass, isSuperUser]);

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD
  const convertDateFormat = (value: any) => {
    if (!value) return '';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '';

    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };

  // Fetch data based on active tab
  useEffect(() => {
    if (mobile && studentClass) {
      fetchDataForTab(activeTab);
    }
  }, [activeTab, mobile, studentClass]);

  const fetchDataForTab = async (tabIndex: number) => {
    if (!mobile || !studentClass) {
      showSnackBar('Phone number is required', 'error');
      return;
    }

    setLoading(true);
    try {
      // Format phone number - add '91' prefix if not present
      // const formattedMobile = mobile.startsWith('91') ? mobile : `91${mobile}`;
      const formattedMobile = mobile;
      // const formattedMobile = '';
      const templateFormType = formTypeMap[tabIndex];
      const response = await aiActions.getRecordEntity(formattedMobile, {
        template_form_type: templateFormType,
        ...(isSuperUser ? {} : { class: studentClass }),
      });
      const payload = response?.data || response;
      if (payload && payload.items) {
        const items = payload.items || [];

        if (!items.length) {
          showSnackBar('No data available for this section', 'info');

          // Clear the appropriate state
          switch (tabIndex) {
            case 0:
              setExamCalendar([]);
              break;
            case 1:
              setTimetableData({});
              break;
            case 2:
              setSyllabus([]);
              break;
            case 3:
              setSchoolMarks([]);
              break;
            case 4:
              setHolidayCalendar([]);
              break;
          }
          return;
        }

        switch (tabIndex) {
          case 0: // Exam Calendar
            setExamCalendar(
              items.map((item, idx) => {
                const entity = item.entity || {};
                return {
                  id: item._id || idx,
                  examName: entity.examname || entity.exam_name || entity.name || '',
                  // board: entity.board || '',
                  class: String(entity?.class || ''),

                  // class: entity?.class || '',
                  section: entity.section || '',
                  subject: entity.subject || '',
                  date: convertDateFormat(entity.date || ''),
                  time: entity.time || '',
                  duration: entity.duration || '',
                  syllabus: entity.syllabus || 'TBD',
                  marks: entity.marks || 100,
                  passingMarks: entity.passingmarks || 0,
                  mode: entity.mode || 'Offline',
                  status: entity.status || 'Not Initiated',
                };
              })
            );

            break;

          case 1: // Timetable
            // Group timetable by class
            const groupedTimetable = items.reduce((acc, item) => {
              const entity = item.entity || {};
              const className = entity.class || '';
              if (!acc[className]) {
                acc[className] = [];
              }
              acc[className].push({
                id: item._id,
                Time: entity.time || '',
                Period: entity.period || '',
                Monday: entity.monday || '',
                Tuesday: entity.tuesday || '',
                Wednesday: entity.wednesday || '',
                Thursday: entity.thursday || '',
                Friday: entity.friday || '',
              });
              return acc;
            }, {});
            setTimetableData(groupedTimetable);

            // Set first available class as selected
            const firstClass = Object.keys(groupedTimetable)[0];
            if (firstClass) {
              setSelectedClass(firstClass);
            }
            break;

          case 2: // Syllabus
            setSyllabus(
              items.map((item, idx) => {
                const entity = item.entity || {};
                return {
                  id: item._id || idx,
                  grade: entity.grade || '',
                  subject: entity.subject || '',
                  chapter: entity.chapter || '',
                  status: entity.status || 'not-started',
                  progress: entity.progress || 0,
                };
              })
            );
            break;

          case 3:
            setSchoolMarks(
              items.map((item) => {
                const e = item.entity || {};

                const obtained = Number(e.obtained_marks || 0);
                const total = Number(e.total_marks || 0);
                const percentage = total ? Math.round((obtained / total) * 100) : 0;

                return {
                  id: item._id,
                  testName: e.type_of_test || '',
                  subject: e.subject || '',
                  class: e.class || '',
                  date: item.created_at,
                  marksObtained: obtained,
                  maxMarks: total,
                  percentage,
                  grade:
                    percentage >= 90
                      ? 'A+'
                      : percentage >= 75
                        ? 'A'
                        : percentage >= 60
                          ? 'B'
                          : percentage >= 40
                            ? 'C'
                            : 'F',
                  //  rank: null,
                };
              })
            );
            break;
          case 4:
            setHolidayCalendar(
              items.map((item, idx) => {
                const e = item.entity || {};
                return {
                  id: item._id || idx,
                  name: e.name || '',
                  date: convertDateFormat(e.date || ''), // 🔥 FIX
                  type: (e.type || 'All').toLowerCase(),
                  description: e.description || '',
                };
              })
            );
            break;
        }

        showSnackBar(`Loaded ${items.length} record(s) successfully`, 'success');
      } else {
        showSnackBar('No data available', 'info');
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      showSnackBar('Failed to fetch data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // setClassFilter('all');
    setSubjectFilter(null);
    setStatusFilter('all');
  }, [activeTab]);

  useEffect(() => {
    if (studentClass) {
      setSelectedClass(studentClass);
      // ✅ only set if not already selected
      setClassFilter((prev) => prev || studentClass);
    }
  }, [studentClass]);

  // Table columns
  const examColumns = [
    { accessorKey: 'examName', header: 'Exam Name', enableSorting: false },
    //  { accessorKey: 'board', header: 'Board', enableSorting: false },
    { accessorKey: 'class', header: 'Class', enableSorting: false },
    { accessorKey: 'section', header: 'Section', enableSorting: false },
    { accessorKey: 'subject', header: 'Subject', enableSorting: false },
    {
      accessorKey: 'date',
      header: 'Date',
      enableSorting: false,
      Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-GB') : '',
    },
    { accessorKey: 'time', header: 'Time', enableSorting: false },
    { accessorKey: 'duration', header: 'Duration', enableSorting: false },
    { accessorKey: 'syllabus', header: 'Syllabus', enableSorting: false },
    { accessorKey: 'marks', header: 'Marks', enableSorting: false },
    { accessorKey: 'passingMarks', header: 'Passing Marks', enableSorting: false },
    { accessorKey: 'mode', header: 'Mode', enableSorting: false },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      Cell: ({ cell }) => getStatusBadge(cell.getValue()),
    },
  ];


  const timetableColumns = [
    { accessorKey: 'Time', header: 'Time', enableSorting: false },
    { accessorKey: 'Period', header: 'Period', enableSorting: false },
    { accessorKey: 'Monday', header: 'Monday', enableSorting: false },
    { accessorKey: 'Tuesday', header: 'Tuesday', enableSorting: false },
    { accessorKey: 'Wednesday', header: 'Wednesday', enableSorting: false },
    { accessorKey: 'Thursday', header: 'Thursday', enableSorting: false },
    { accessorKey: 'Friday', header: 'Friday', enableSorting: false },
  ];

  const syllabusColumns = [
    { accessorKey: 'grade', header: 'Grade', enableSorting: false },
    { accessorKey: 'subject', header: 'Subject', enableSorting: false },
    {
      accessorKey: 'chapter',
      header: 'Chapter',
      enableSorting: false,
      Cell: ({ cell }) => <EllipsisWithTooltip text={cell.getValue()} maxWidth={220} />,
    },
    {
      accessorKey: 'progress',
      header: 'Progress',
      enableSorting: false,
      Cell: ({ row }) => (
        <div style={{ width: '100%' }}>
          <div style={{ height: 6, background: '#e0e0e0', borderRadius: 4 }}>
            <div
              style={{
                width: `${row.original.progress}%`,
                height: '100%',
                background: row.original.status === 'completed' ? '#4caf50' : '#ff9800',
                borderRadius: 4,
              }}
            />
          </div>
          <small>{row.original.progress}%</small>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      Cell: ({ cell }) => getStatusBadge(cell.getValue()),
    },
  ];

  const marksColumns = [
    { accessorKey: 'testName', header: 'Test', enableSorting: false },
    { accessorKey: 'subject', header: 'Subject', enableSorting: false },
    {
      accessorKey: 'date',
      header: 'Date',
      enableSorting: false,
      Cell: ({ cell }) => {
        const dateValue = cell.getValue();
        return dateValue ? new Date(dateValue).toLocaleDateString('en-GB') : '';
      },
    },
    {
      header: 'Marks',
      enableSorting: false,
      Cell: ({ row }) => `${row.original.marksObtained}/${row.original.maxMarks}`,
    },
    {
      accessorKey: 'percentage',
      header: '%',
      enableSorting: false,
      Cell: ({ cell }) => <strong>{cell.getValue()}%</strong>,
    },
    { accessorKey: 'grade', header: 'Grade', enableSorting: false },
    // {
    //   accessorKey: 'rank',
    //   header: 'Rank',
    //   enableSorting: false,
    //   Cell: ({ cell }) => `#${cell.getValue()}`,
    // },
  ];

  const holidayColumns = [
    { accessorKey: 'name', header: 'Holiday', enableSorting: false },
    {
      accessorKey: 'date',
      header: 'Date',
      enableSorting: false,
      Cell: ({ cell }) => {
        const dateValue = cell.getValue();
        return dateValue ? new Date(dateValue).toLocaleDateString('en-GB') : '';
      },
    },
    { accessorKey: 'type', header: 'Type', enableSorting: false },
    { accessorKey: 'description', header: 'Description', enableSorting: false },
  ];

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#e6f7ed', color: '#2e7d32' },
      'in-progress': { bg: '#fff4e6', color: '#ed6c02' },
      'not-started': { bg: '#ffebee', color: '#d32f2f' },
      upcoming: { bg: '#e3f2fd', color: '#1976d2' },
    };
    const style = colors[status] || { bg: '#f5f5f5', color: '#757575' };
    return (
      <span
        style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: 600,
          backgroundColor: style.bg,
          color: style.color,
          display: 'inline-block',
        }}
      >
        {status}
      </span>
    );
  };

  const subjects = [
    'all',
    ...Array.from(new Set(examCalendar.map((e) => e.subject).filter(Boolean))),
  ];
  const classes = [
    ...(isSuperUser ? ['all'] : []),
    ...Array.from(new Set(examCalendar.map((e) => e.class).filter(Boolean))),
  ];

  const marksSubjects = [
    'all',
    ...Array.from(new Set(schoolMarks.map((m) => m.subject).filter(Boolean))),
  ];

  const marksClasses = [
    ...(isSuperUser ? ['all'] : []),
    ...Array.from(new Set(schoolMarks.map((m) => m.class).filter(Boolean))),
  ];

  const availableClasses = Object.keys(timetableData);

  // Filtered data
  const filteredExams = examCalendar.filter((exam) => {
    return (
      (examFilter === 'all' || exam.subject === examFilter) &&
      (statusFilter === 'all' || exam.status === statusFilter) &&
      (
        isSuperUser
          ? (classFilter === 'all' || String(exam.class) === String(classFilter))
          : String(exam.class) === String(studentClass)
      )
    );
  });

  const filteredTimetable = timetableData[selectedClass] || [];

  const filteredSyllabus = syllabus.filter(
    (item) =>
      (subjectFilter === 'all' || item.subject === subjectFilter) &&
      (statusFilter === 'all' || item.status === statusFilter)
  );
  const filteredMarks = schoolMarks.filter((mark) => {
    return (
      (subjectFilter === null || mark.subject === subjectFilter) &&
      (
        isSuperUser
          ? (classFilter === 'all' || String(mark.class) === String(classFilter))
          : String(mark.class) === String(studentClass)
      )
    );
  });

  const filteredHolidays = holidayCalendar.filter(
    (holiday) => holidayTypeFilter === null || holiday.type === holidayTypeFilter
  );

  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, onClick: () => navigate("/") },
    { label: 'Academic Info' },
  ];

  return (
    <div style={containerStyle}>
      <DynamicBreadcrumbs items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => {
          if (item?.onClick) {
            item.onClick();
          }
        }} />
      <Grid item xs={4} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>Academic Info</Typography>
      </Grid>
      <div style={cardStyle}>
        {/* Tabs */}
        <div style={tabContainerStyle}>
          {['Exam Calendar', 'Timetable', 'Syllabus', 'Marks', 'Holidays'].map((tab, idx) => (
            <button
              key={idx}
              onClick={() => setActiveTab(idx)}
              style={{
                ...tabButtonStyle,
                borderBottom: activeTab === idx ? '3px solid #667eea' : 'none',
                color: activeTab === idx ? '#667eea' : '#666',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div style={contentStyle}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Exam Calendar Tab */}
              {activeTab === 0 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Left spacer for responsiveness */}
                    <Grid item xs={12} md={3} />

                    {/* Right filters */}
                    <Grid item xs={12} md={9}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          justifyContent: 'flex-end',
                          flexWrap: { xs: 'wrap', md: 'nowrap' }, // Wrap on small screens
                        }}
                      >
                        {/* Class Filter */}
                        <SearchableDropdown
                          options={classes
                            .filter((c) => c !== 'all')
                            .map((c) => ({ label: c, value: c }))}
                          label="Class"
                          value={
                            classFilter === 'all'
                              ? null
                              : { label: classFilter, value: classFilter }
                          }
                          onChange={(val) => setClassFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 140 }}
                          disabled={!isSuperUser}
                        />

                        {/* Subject Filter */}
                        <SearchableDropdown
                          options={subjects
                            .filter((s) => s !== 'all')
                            .map((s) => ({ label: s, value: s }))}
                          label="Subject"
                          value={
                            examFilter === 'all' ? null : { label: examFilter, value: examFilter }
                          }
                          onChange={(val) => setExamFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 140 }}
                        />

                        <SearchableDropdown
                          options={[
                            { label: 'Upcoming', value: 'upcoming' },
                            { label: 'Completed', value: 'completed' },
                          ]}
                          label="Status"
                          value={statusFilter === 'all' ? null : { label: statusFilter, value: statusFilter }}
                          onChange={(val) => setStatusFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 150 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>


                  <CommonTable
                    columns={examColumns}
                    data={filteredExams}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                    muiTableContainerProps={{
                      sx: { maxHeight: 'none', overflowY: 'scroll', height: '68vh' },
                    }}
                  />
                </>
              )}

              {/* Timetable Tab */}
              {activeTab === 1 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={9} />
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <SearchableDropdown
                          options={availableClasses.map((cls) => ({
                            label: cls,
                            value: cls,
                          }))}
                          label="Class"
                          value={{ label: selectedClass, value: selectedClass }}
                          // onChange={(val) => setSelectedClass(val ? val.value : 'Class 6')}
                          onChange={() => { }}
                          disabled={true}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <CommonTable
                    columns={timetableColumns}
                    data={filteredTimetable}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                    muiTableContainerProps={{
                      sx: { maxHeight: 'none', overflowY: 'hidden' },
                    }}
                  />
                </>
              )}

              {/* Syllabus Tab */}
              {activeTab === 2 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Left empty space */}
                    <Grid item xs={12} md={6} />

                    {/* Right filters */}
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 1,
                          flexWrap: 'nowrap',
                        }}
                      >
                        <SearchableDropdown
                          options={subjects
                            .filter((s) => s !== 'all')
                            .map((s) => ({ label: s, value: s }))}
                          label="Subject"
                          value={
                            subjectFilter === 'all'
                              ? null
                              : { label: subjectFilter, value: subjectFilter }
                          }
                          onChange={(val) => setSubjectFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 140 }}
                        />

                        <SearchableDropdown
                          options={[
                            { label: 'Completed', value: 'completed' },
                            { label: 'In Progress', value: 'in-progress' },
                            { label: 'Not Started', value: 'not-started' },
                          ]}
                          label="Status"
                          value={
                            statusFilter === 'all'
                              ? null
                              : { label: statusFilter, value: statusFilter }
                          }
                          onChange={(val) => setStatusFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 140 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <CommonTable
                    columns={syllabusColumns}
                    data={filteredSyllabus}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                    muiTableContainerProps={{
                      sx: { maxHeight: 'none', overflowY: 'hidden' },
                    }}
                  />
                </>
              )}

              {/* Marks Tab */}
              {activeTab === 3 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6} />

                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>

                        {/* Class */}
                        <SearchableDropdown
                          options={marksClasses.filter(c => c !== 'all').map(c => ({ label: `Class ${c}`, value: c }))}
                          label="Class"
                          value={classFilter === 'all' ? null : { label: `Class ${classFilter}`, value: classFilter }}
                          onChange={(val) => setClassFilter(val ? val.value : 'all')}
                          size="small"
                          sx={{ width: 140 }}
                          disabled={!isSuperUser}
                        />

                        {/* Subject */}
                        <SearchableDropdown
                          options={marksSubjects.filter(s => s !== 'all').map(s => ({ label: s, value: s }))}
                          label="Subject"
                          value={subjectFilter === null ? null : { label: subjectFilter, value: subjectFilter }}
                          onChange={(val) => setSubjectFilter(val ? val.value : null)}
                          size="small"
                          sx={{ width: 140 }}
                        />

                      </Box>
                    </Grid>
                  </Grid>

                  <CommonTable
                    columns={marksColumns}
                    data={filteredMarks}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                  />
                </>
              )}


              {/* Holidays Tab */}
              {activeTab === 4 && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={9} />
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <SearchableDropdown
                          options={[
                            { label: 'All', value: 'all' },
                            { label: 'National', value: 'national' },
                            { label: 'Cultural', value: 'cultural' },
                            { label: 'Religious', value: 'religious' },
                            { label: 'School', value: 'school' },
                          ]}
                          label="Type"
                          value={
                            holidayTypeFilter === null
                              ? { label: 'All', value: 'all' }
                              : { label: holidayTypeFilter, value: holidayTypeFilter }
                          }
                          onChange={(val) => {
                            if (!val || val.value === 'all') {
                              setHolidayTypeFilter(null);
                            } else {
                              setHolidayTypeFilter(val.value);
                            }
                          }}
                          size="small"
                          sx={{ width: '100%' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <CommonTable
                    columns={holidayColumns}
                    data={filteredHolidays}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enablePagination={false}
                    enableSorting={false}
                    enableColumnFilters={false}
                    muiTableContainerProps={{
                      sx: {
                       height: 'calc(100vh - 300px)',
                        width: '100%',
                        maxHeight: '600px', overflowY: 'hidden'
                      },
                    }}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '1px',
  backgroundColor: '#fafafa',
  minHeight: '85vh',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const cardStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
};

const tabContainerStyle = {
  display: 'flex',
  borderBottom: '2px solid #e0e0e0',
  overflowX: 'auto',
};

const tabButtonStyle = {
  padding: '12px 20px',
  border: 'none',
  background: 'none',
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s',
};

const contentStyle = {
  padding: '16px',
};

export default StudentDashboard;
